import type {
  ComponentPropsWithRef,
  ElementType,
  ForwardedRef,
  ReactNode,
} from 'react';
import { forwardRef } from 'react';

import classNames from 'classnames';

import type { DistributiveOmit } from 'types/distributiveOmit';
import type { FixedForwardRef } from 'types/fixedForward';

import { Layers } from '../Layers/Layers';
import { Spinner } from '../Spinner/Spinner';

import styles from './Button.module.css';

type ButtonBaseProps = {
  appearance?:
    | 'primary'
    | 'primary-brand'
    | 'primary-destructive'
    | 'secondary'
    | 'transparent-brand'
    | 'transparent-destructive'
    | 'transparent';
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  size?: 'large' | 'default' | 'small' | 'xsmall';
  shape?: 'circle' | 'square';
  fullWidth?: boolean;
  busy?: boolean;
  disabled?: boolean;
  telemetryActionNameWithNoPii?: string;
};

/**
 *  Types that allows us to pass in a component as a prop
 *  without losing the original props of the component.
 *  https://www.totaltypescript.com/pass-component-as-prop-react
 */
type WrappedComponentProps<ComponentProp extends ElementType> = {
  as?: ComponentProp;
} & DistributiveOmit<
  ComponentPropsWithRef<
    ElementType extends ComponentProp ? 'button' : ComponentProp
  >,
  'as'
>;

export type ButtonComponentProps<ComponentProp extends ElementType> =
  WrappedComponentProps<ComponentProp> & ButtonBaseProps;

export type ButtonProps = ButtonComponentProps<'button'>;

const fixedForwardRef = forwardRef as FixedForwardRef;

const UnwrappedButtonComponent = <ComponentProp extends ElementType>(
  props: ButtonComponentProps<ComponentProp>,
  ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
) => {
  const {
    appearance = 'secondary',
    as: Component = 'button',
    busy,
    children,
    className,
    disabled,
    fullWidth = false,
    leftSection,
    rightSection,
    shape,
    size = 'default',
    type,
    telemetryActionNameWithNoPii,
    ...otherProps
  } = props;

  const elementClasses = classNames(
    styles.root,
    styles[appearance],
    styles[size],
    shape && styles[shape],
    className,
    { [styles.withLeftSection]: leftSection },
    { [styles.withRightSection]: rightSection },
    { [styles.fullWidth]: fullWidth },
  );

  const showSpinner = busy ?? false;

  const isAnchor = Component === 'a';
  const shouldDisableAnchor = isAnchor && disabled;

  let componentProps = otherProps;

  if (Component === 'button') {
    componentProps = {
      type: type || 'button' || busy,
      disabled,
      ...otherProps,
    };
  } else if (shouldDisableAnchor) {
    // For disabled anchors, remove navigation props and add accessibility attributes
    const { href: _href, target: _target, ...restProps } = otherProps as any;

    componentProps = {
      ...restProps,
      'aria-disabled': true,
      onClick: (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      },
    };
  } else {
    componentProps = { ...otherProps };
  }

  const defaultProps = componentProps;

  return (
    <Component
      className={elementClasses}
      ref={ref}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...defaultProps}
      // TODO: https://glossgenius.atlassian.net/browse/EN-40897
      telemetry-action-name={telemetryActionNameWithNoPii}
    >
      {leftSection}
      <Layers as="span">
        <Layers.Layer
          as="span"
          className={classNames({ [styles.hidden]: showSpinner })}
          aria-hidden={showSpinner}
        >
          <span className={styles.content}>{children}</span>
        </Layers.Layer>
        {showSpinner && (
          <Layers.Layer as="span" className={styles.busy}>
            <span className={styles.content}>
              <Spinner />
            </span>
          </Layers.Layer>
        )}
      </Layers>
      {rightSection}
    </Component>
  );
};

UnwrappedButtonComponent.displayName = 'Button';

export const Button = fixedForwardRef(UnwrappedButtonComponent);
